/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving pharmacies.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialPharmaciesState {
    pharmacies: Dtos.Pharmacy[] | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
};

export interface IPharmaciesState {
    pharmacies: IInitialPharmaciesState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const PharmaciesInitialState: IInitialPharmaciesState = {
    pharmacies: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const usePharmaciesSelector: TypedUseSelectorHook<IPharmaciesState> = useSelector;

export const PharmaciesSelectors = {
    selectPharmacies: (state: IPharmaciesState) => state.pharmacies.pharmacies,
    selectLoadState: (state: IPharmaciesState) => state.pharmacies.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class PharmaciesReducer extends ImmerReducer<IInitialPharmaciesState> {
    public load() {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(pharmacies?: Dtos.Pharmacy[]) {
        this.draftState.pharmacies = pharmacies ? pharmacies : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.pharmacies = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...PharmaciesInitialState };
    }
};

export const PharmaciesActions = createActionCreators(PharmaciesReducer);
const reducer = createReducerFunction(PharmaciesReducer, PharmaciesInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const PharmaciesApi = {
    load: () => {
        return client.get(new Dtos.GetPharmacies());
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const PharmaciesLogic = {
    load: createLogic<IPharmaciesState, {}, undefined, string, ReturnType<typeof PharmaciesActions.load>>({
        type: PharmaciesActions.load.type,
        process: async ({ action }, dispatch, done) => {
            try {
                const response = await PharmaciesApi.load();

                dispatch(PharmaciesActions.loadSuccess(
                    response.pharmacies
                ));
            }
            catch (error: any) {
                dispatch(PharmaciesActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    PharmaciesLogic.load
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const usePharmacies = (): [Dtos.Pharmacy[] | null, IRequestState<Dtos.ResponseStatus>, typeof PharmaciesActions] => {
    const pharmacies = useSelector(PharmaciesSelectors.selectPharmacies);
    const loadState = useSelector(PharmaciesSelectors.selectLoadState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof PharmaciesActions>(() => {
        return bindActionCreators(PharmaciesActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(PharmaciesActions, dispatch));
    }, [dispatch, setActions]);

    return [
        pharmacies,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('pharmacies', reducer as any, logics as any);